import { z } from "zod";

/**
 * Le moteur de layout : un arbre de containers flex dont les feuilles
 * dépendent de l'usage (cf. SPECS/layout-engine.md).
 *
 * - Custom node : les feuilles sont ses champs, des textes, des séparateurs
 *   (cf. `convex/config/templateConfig.ts`).
 * - Frame à espaces : les feuilles sont des espaces qui rangent d'autres
 *   nodes du canvas.
 *
 * Ce module ne connaît que les containers. Il est générique sur la feuille,
 * qui n'a qu'à porter un `kind` (jamais `"container"`) et un `id`. Toutes les
 * opérations sont pures et immuables : partagées par l'éditeur, la
 * validation serveur et, à terme, les tools de Nolë.
 */

export type LayoutLeaf = { kind: string; id: string };

export type LayoutContainer<Leaf extends LayoutLeaf = LayoutLeaf> = {
  kind: "container";
  id: string;
  direction: "row" | "column";
  gap?: number;
  align?: "start" | "center" | "end" | "stretch";
  justify?: "start" | "center" | "end" | "between";
  padding?: number;
  grow?: number;
  children: Array<LayoutTreeNode<Leaf>>;
};

export type LayoutTreeNode<Leaf extends LayoutLeaf = LayoutLeaf> =
  | LayoutContainer<Leaf>
  | Leaf;

export const MAX_CHILDREN_PER_CONTAINER = 20;

/**
 * Garde de type plutôt qu'un test de `kind` en place : sur une feuille
 * générique, `kind` vaut `string`, et `node.kind === "container"` ne
 * suffirait pas à TypeScript pour écarter la feuille.
 */
export function isLayoutContainer<Leaf extends LayoutLeaf>(
  node: LayoutTreeNode<Leaf>,
): node is LayoutContainer<Leaf> {
  return node.kind === "container";
}

// ── Schéma ──────────────────────────────────────────────────────────────

/**
 * Le schéma zod d'un arbre dont les feuilles valident `leafSchema`.
 *
 * Stocké en `v.any()` côté Convex (pas de validateurs récursifs) : ce parse
 * est obligatoire à chaque écriture, sinon un arbre malformé casserait le
 * rendu chez tous les viewers.
 */
export function buildLayoutContainerSchema<Leaf extends LayoutLeaf>(
  leafSchema: z.ZodType<Leaf>,
): z.ZodType<LayoutContainer<Leaf>> {
  const containerSchema: z.ZodType<LayoutContainer<Leaf>> = z.strictObject({
    kind: z.literal("container"),
    id: z.string().min(1).max(64),
    direction: z.enum(["row", "column"]),
    gap: z.number().min(0).max(100).optional(),
    align: z.enum(["start", "center", "end", "stretch"]).optional(),
    justify: z.enum(["start", "center", "end", "between"]).optional(),
    padding: z.number().min(0).max(100).optional(),
    grow: z.number().min(0).max(12).optional(),
    children: z
      .array(
        z.lazy(
          (): z.ZodType<LayoutTreeNode<Leaf>> =>
            z.union([containerSchema, leafSchema]),
        ),
      )
      .max(MAX_CHILDREN_PER_CONTAINER),
  });
  return containerSchema;
}

// ── Création ────────────────────────────────────────────────────────────

/** Ids stables (containers, feuilles) : jamais réutilisés ni modifiés. */
export function genLayoutId(prefix: string): string {
  return `${prefix}_${crypto.randomUUID().replace(/-/g, "").slice(0, 10)}`;
}

export function newLayoutContainer<Leaf extends LayoutLeaf>(
  direction: "row" | "column",
): LayoutContainer<Leaf> {
  return {
    kind: "container",
    id: genLayoutId("c"),
    direction,
    gap: 8,
    children: [],
  };
}

// ── Parcours ────────────────────────────────────────────────────────────

/** Les feuilles de l'arbre, dans l'ordre du parcours. */
export function collectLayoutLeaves<Leaf extends LayoutLeaf>(
  tree: LayoutContainer<Leaf>,
): Leaf[] {
  return tree.children.flatMap((child) =>
    isLayoutContainer(child) ? collectLayoutLeaves(child) : [child],
  );
}

/** Tous les ids de l'arbre, racine comprise. */
export function collectLayoutNodeIds<Leaf extends LayoutLeaf>(
  tree: LayoutContainer<Leaf>,
): string[] {
  return [
    tree.id,
    ...tree.children.flatMap((child) =>
      isLayoutContainer(child) ? collectLayoutNodeIds(child) : [child.id],
    ),
  ];
}

/** Profondeur d'imbrication : ne compte que les containers. */
export function getLayoutDepth<Leaf extends LayoutLeaf>(
  tree: LayoutContainer<Leaf>,
): number {
  return (
    1 +
    Math.max(
      0,
      ...tree.children.map((child) =>
        isLayoutContainer(child) ? getLayoutDepth(child) : 0,
      ),
    )
  );
}

export function findLayoutNode<Leaf extends LayoutLeaf>(
  tree: LayoutContainer<Leaf>,
  nodeId: string,
): LayoutTreeNode<Leaf> | null {
  if (tree.id === nodeId) return tree;
  for (const child of tree.children) {
    if (child.id === nodeId) return child;
    if (isLayoutContainer(child)) {
      const found = findLayoutNode(child, nodeId);
      if (found) return found;
    }
  }
  return null;
}

export function findLayoutParentId<Leaf extends LayoutLeaf>(
  tree: LayoutContainer<Leaf>,
  nodeId: string,
): string | null {
  for (const child of tree.children) {
    if (child.id === nodeId) return tree.id;
    if (isLayoutContainer(child)) {
      const found = findLayoutParentId(child, nodeId);
      if (found) return found;
    }
  }
  return null;
}

/**
 * Chemin racine → node, ids inclus, le node lui-même en dernier. Vide si le
 * node est introuvable.
 */
export function getLayoutAncestorPath<Leaf extends LayoutLeaf>(
  tree: LayoutContainer<Leaf>,
  nodeId: string,
): string[] {
  const walk = (node: LayoutTreeNode<Leaf>, trail: string[]): string[] => {
    const path = [...trail, node.id];
    if (node.id === nodeId) return path;
    if (!isLayoutContainer(node)) return [];
    for (const child of node.children) {
      const found = walk(child, path);
      if (found.length > 0) return found;
    }
    return [];
  };
  return walk(tree, []);
}

// ── Opérations immuables ────────────────────────────────────────────────

/** Applique `fn` à chaque container, de la racine vers les feuilles. */
function mapContainers<Leaf extends LayoutLeaf>(
  tree: LayoutContainer<Leaf>,
  fn: (container: LayoutContainer<Leaf>) => LayoutContainer<Leaf>,
): LayoutContainer<Leaf> {
  const next = fn(tree);
  return {
    ...next,
    children: next.children.map((child) =>
      isLayoutContainer(child) ? mapContainers(child, fn) : child,
    ),
  };
}

export function updateLayoutNode<Leaf extends LayoutLeaf>(
  tree: LayoutContainer<Leaf>,
  nodeId: string,
  // Partiel sur l'union : l'appelant connaît le kind du nœud qu'il patche,
  // cette fonction n'a pas à le redémontrer.
  patch: Partial<LayoutTreeNode<Leaf>>,
): LayoutContainer<Leaf> {
  const patchNode = <T extends LayoutTreeNode<Leaf>>(node: T): T =>
    node.id === nodeId ? ({ ...node, ...patch } as T) : node;
  return mapContainers(patchNode(tree), (container) => ({
    ...container,
    children: container.children.map(patchNode),
  }));
}

export function removeLayoutNode<Leaf extends LayoutLeaf>(
  tree: LayoutContainer<Leaf>,
  nodeId: string,
): { tree: LayoutContainer<Leaf>; removed: LayoutTreeNode<Leaf> | null } {
  const removed = tree.id === nodeId ? null : findLayoutNode(tree, nodeId);
  if (!removed) return { tree, removed: null };
  return {
    tree: mapContainers(tree, (container) => ({
      ...container,
      children: container.children.filter((child) => child.id !== nodeId),
    })),
    removed,
  };
}

/** `index` absent ou hors bornes : en fin (ou en tête) du container. */
export function insertLayoutNode<Leaf extends LayoutLeaf>(
  tree: LayoutContainer<Leaf>,
  containerId: string,
  index: number | undefined,
  node: LayoutTreeNode<Leaf>,
): LayoutContainer<Leaf> {
  return mapContainers(tree, (container) => {
    if (container.id !== containerId) return container;
    const children = [...container.children];
    const at =
      index === undefined
        ? children.length
        : Math.max(0, Math.min(index, children.length));
    children.splice(at, 0, node);
    return { ...container, children };
  });
}

/**
 * Déplace un node vers (containerId, index). Refuse — arbre inchangé — de
 * déplacer la racine, ou un container dans lui-même ou un descendant.
 */
export function moveLayoutNode<Leaf extends LayoutLeaf>(
  tree: LayoutContainer<Leaf>,
  nodeId: string,
  targetContainerId: string,
  targetIndex: number | undefined,
): LayoutContainer<Leaf> {
  const node = findLayoutNode(tree, nodeId);
  if (!node || node.id === tree.id) return tree;
  if (isLayoutContainer(node) && findLayoutNode(node, targetContainerId)) {
    return tree;
  }

  const { tree: without, removed } = removeLayoutNode(tree, nodeId);
  if (!removed || !findLayoutNode(without, targetContainerId)) return tree;
  return insertLayoutNode(without, targetContainerId, targetIndex, removed);
}

/** Où lâcher un nœud par rapport à un autre, dans une vue en arbre. */
export type LayoutDropPosition = "before" | "after" | "inside";

/**
 * Place un ou plusieurs nœuds avant ou après un autre, ou à la fin d'un
 * container. Plusieurs nœuds arrivent ensemble, dans l'ordre de l'arbre ; une
 * boîte emporte son contenu. Sans effet si la cible est l'un d'eux ou dans
 * l'un d'eux, si l'on vise à côté de la racine, ou si le container d'arrivée
 * déborderait.
 */
export function placeLayoutNodes<Leaf extends LayoutLeaf>(
  tree: LayoutContainer<Leaf>,
  nodeIds: string[],
  targetId: string,
  position: LayoutDropPosition,
): LayoutContainer<Leaf> {
  const selected = new Set(outermostLayoutIds(tree, nodeIds));
  const moving = collectLayoutNodeIds(tree).filter((id) => selected.has(id));
  if (moving.length === 0 || selected.has(tree.id)) return tree;
  if (getLayoutAncestorPath(tree, targetId).some((id) => selected.has(id))) {
    return tree;
  }

  const containerId =
    position === "inside" ? targetId : findLayoutParentId(tree, targetId);
  const container = containerId ? findLayoutNode(tree, containerId) : null;
  if (!container || !isLayoutContainer(container)) return tree;
  const arriving = moving.filter(
    (id) => findLayoutParentId(tree, id) !== container.id,
  ).length;
  if (container.children.length + arriving > MAX_CHILDREN_PER_CONTAINER) {
    return tree;
  }

  let next = tree;
  const removed: Array<LayoutTreeNode<Leaf>> = [];
  for (const id of moving) {
    const result = removeLayoutNode(next, id);
    if (!result.removed) return tree;
    next = result.tree;
    removed.push(result.removed);
  }

  const siblings = (findLayoutNode(next, container.id) as LayoutContainer<Leaf>)
    .children;
  const targetIndex = siblings.findIndex((child) => child.id === targetId);
  const index =
    position === "inside"
      ? siblings.length
      : position === "before"
        ? targetIndex
        : targetIndex + 1;
  return removed.reduce<LayoutContainer<Leaf>>(
    (current, node, offset) =>
      insertLayoutNode(current, container.id, index + offset, node),
    next,
  );
}

export function placeLayoutNode<Leaf extends LayoutLeaf>(
  tree: LayoutContainer<Leaf>,
  nodeId: string,
  targetId: string,
  position: LayoutDropPosition,
): LayoutContainer<Leaf> {
  return placeLayoutNodes(tree, [nodeId], targetId, position);
}

/**
 * Entoure d'une boîte un ou plusieurs frères d'un même container, à la place
 * du premier, dans leur ordre. Sans effet (`containerId: null`) si ce ne sont
 * pas des frères, si la racine en est, ou si la profondeur dépasse la limite.
 * Refuser au geste plutôt qu'à la validation : l'erreur ne tomberait qu'à
 * l'enregistrement, loin du geste qui l'a causée.
 */
export function wrapLayoutNodes<Leaf extends LayoutLeaf>(
  tree: LayoutContainer<Leaf>,
  nodeIds: string[],
  direction: "row" | "column",
  maxDepth: number,
): { tree: LayoutContainer<Leaf>; containerId: string | null } {
  const unchanged = { tree, containerId: null };
  const parentIds = new Set(nodeIds.map((id) => findLayoutParentId(tree, id)));
  const [parentId] = parentIds;
  if (parentIds.size !== 1 || !parentId) return unchanged;

  const parent = findLayoutNode(tree, parentId) as LayoutContainer<Leaf>;
  const ids = new Set(nodeIds);
  const container: LayoutContainer<Leaf> = {
    ...newLayoutContainer<Leaf>(direction),
    children: parent.children.filter((child) => ids.has(child.id)),
  };
  const children = parent.children.filter((child) => !ids.has(child.id));
  children.splice(
    parent.children.findIndex((child) => ids.has(child.id)),
    0,
    container,
  );
  const next = updateLayoutNode(tree, parentId, {
    children,
  } as Partial<LayoutTreeNode<Leaf>>);
  if (getLayoutDepth(next) > maxDepth) return unchanged;
  return { tree: next, containerId: container.id };
}

export function wrapLayoutNode<Leaf extends LayoutLeaf>(
  tree: LayoutContainer<Leaf>,
  nodeId: string,
  direction: "row" | "column",
  maxDepth: number,
): { tree: LayoutContainer<Leaf>; containerId: string | null } {
  return wrapLayoutNodes(tree, [nodeId], direction, maxDepth);
}

/** Une copie du nœud, ids neufs à tous les étages. */
function cloneLayoutNode<Leaf extends LayoutLeaf>(
  node: LayoutTreeNode<Leaf>,
): LayoutTreeNode<Leaf> {
  if (!isLayoutContainer(node)) {
    return { ...node, id: genLayoutId(node.kind.charAt(0)) };
  }
  return {
    ...node,
    id: genLayoutId("c"),
    children: node.children.map(cloneLayoutNode),
  };
}

/**
 * Les nœuds dont aucun ancêtre n'est aussi dans la liste : dupliquer ou
 * supprimer une boîte emporte déjà son contenu.
 */
function outermostLayoutIds<Leaf extends LayoutLeaf>(
  tree: LayoutContainer<Leaf>,
  nodeIds: string[],
): string[] {
  const ids = new Set(nodeIds);
  return nodeIds.filter((id) =>
    getLayoutAncestorPath(tree, id)
      .slice(0, -1)
      .every((ancestorId) => !ids.has(ancestorId)),
  );
}

/**
 * Duplique des nœuds, chaque copie juste après son original. `null` si la
 * racine en est, si un nœud est introuvable, ou si un container dépasserait
 * sa limite d'enfants.
 */
export function duplicateLayoutNodes<Leaf extends LayoutLeaf>(
  tree: LayoutContainer<Leaf>,
  nodeIds: string[],
): { tree: LayoutContainer<Leaf>; copyIds: string[] } | null {
  let next = tree;
  const copyIds: string[] = [];
  for (const id of outermostLayoutIds(tree, nodeIds)) {
    const parentId = findLayoutParentId(next, id);
    if (!parentId) return null;
    const parent = findLayoutNode(next, parentId) as LayoutContainer<Leaf>;
    if (parent.children.length >= MAX_CHILDREN_PER_CONTAINER) return null;
    const index = parent.children.findIndex((child) => child.id === id);
    const copy = cloneLayoutNode(parent.children[index]);
    next = insertLayoutNode(next, parentId, index + 1, copy);
    copyIds.push(copy.id);
  }
  return { tree: next, copyIds };
}

/** Retire plusieurs nœuds (jamais la racine). */
export function removeLayoutNodes<Leaf extends LayoutLeaf>(
  tree: LayoutContainer<Leaf>,
  nodeIds: string[],
): LayoutContainer<Leaf> {
  return outermostLayoutIds(tree, nodeIds).reduce(
    (current, id) => removeLayoutNode(current, id).tree,
    tree,
  );
}

/**
 * Où insérer à partir d'une sélection : DANS un container sélectionné (en
 * fin), juste APRÈS une feuille sélectionnée, à la racine sans sélection.
 */
export function resolveLayoutInsertionPoint<Leaf extends LayoutLeaf>(
  tree: LayoutContainer<Leaf>,
  selectedId: string | null,
): { containerId: string; index: number | undefined } {
  const selected = selectedId ? findLayoutNode(tree, selectedId) : null;
  if (!selected) return { containerId: tree.id, index: undefined };
  if (isLayoutContainer(selected)) {
    return { containerId: selected.id, index: undefined };
  }

  const parentId = findLayoutParentId(tree, selected.id);
  if (!parentId) return { containerId: tree.id, index: undefined };
  const parent = findLayoutNode(tree, parentId) as LayoutContainer<Leaf>;
  const index = parent.children.findIndex((child) => child.id === selected.id);
  return { containerId: parentId, index: index < 0 ? undefined : index + 1 };
}

/** Retire toutes les feuilles qui vérifient `predicate`. */
export function removeLayoutLeaves<Leaf extends LayoutLeaf>(
  tree: LayoutContainer<Leaf>,
  predicate: (leaf: Leaf) => boolean,
): LayoutContainer<Leaf> {
  return mapContainers(tree, (container) => ({
    ...container,
    children: container.children.filter(
      (child) => isLayoutContainer(child) || !predicate(child),
    ),
  }));
}

/**
 * Le dépôt d'un glisser dans l'éditeur : `activeId` lâché sur `overId`.
 *
 * - `into:<containerId>` (cf. `INTO_PREFIX` côté éditeur) : dans ce container,
 *   vide par construction, en premier enfant.
 * - la racine elle-même (sa bande de gap ou son padding) : en dernier.
 * - un frère du même container : réordonné à sa place (sémantique
 *   `arrayMove`).
 * - un node d'un autre container : inséré à sa place.
 *
 * Rend l'arbre inchangé quand le dépôt ne veut rien dire — `moveLayoutNode`
 * refuse déjà de mettre un container dans lui-même.
 */
export function dropLayoutNode<Leaf extends LayoutLeaf>(
  tree: LayoutContainer<Leaf>,
  activeId: string,
  overId: string,
  intoPrefix: string,
): LayoutContainer<Leaf> {
  if (activeId === overId) return tree;

  if (overId.startsWith(intoPrefix)) {
    const containerId = overId.slice(intoPrefix.length);
    if (containerId === activeId) return tree;
    return moveLayoutNode(tree, activeId, containerId, undefined);
  }
  if (overId === tree.id) {
    return moveLayoutNode(tree, activeId, tree.id, undefined);
  }

  const overParentId = findLayoutParentId(tree, overId);
  if (!overParentId) return tree;
  const overParent = findLayoutNode(
    tree,
    overParentId,
  ) as LayoutContainer<Leaf>;
  const overIndex = overParent.children.findIndex((c) => c.id === overId);

  if (findLayoutParentId(tree, activeId) === overParentId) {
    const children = [...overParent.children];
    const from = children.findIndex((c) => c.id === activeId);
    const [moved] = children.splice(from, 1);
    children.splice(overIndex, 0, moved);
    return updateLayoutNode(tree, overParentId, { children } as Partial<
      LayoutTreeNode<Leaf>
    >);
  }
  return moveLayoutNode(tree, activeId, overParentId, Math.max(0, overIndex));
}

/**
 * `dropLayoutNode` pour une sélection : l'élément saisi (`activeId`) emporte
 * les autres, déposés ensemble dans l'ordre de l'arbre (cf.
 * `placeLayoutNodes`). Même lecture de la cible : dans un container vide (ou
 * la racine), sinon à la place de l'élément survolé — après lui si l'on
 * descend dans la même boîte, avant sinon.
 */
export function dropLayoutNodes<Leaf extends LayoutLeaf>(
  tree: LayoutContainer<Leaf>,
  activeId: string,
  nodeIds: string[],
  overId: string,
  intoPrefix: string,
): LayoutContainer<Leaf> {
  if (nodeIds.length <= 1) {
    return dropLayoutNode(tree, activeId, overId, intoPrefix);
  }
  if (overId.startsWith(intoPrefix)) {
    return placeLayoutNodes(
      tree,
      nodeIds,
      overId.slice(intoPrefix.length),
      "inside",
    );
  }
  if (overId === tree.id) {
    return placeLayoutNodes(tree, nodeIds, tree.id, "inside");
  }

  const parentId = findLayoutParentId(tree, overId);
  if (!parentId) return tree;
  const siblings = (findLayoutNode(tree, parentId) as LayoutContainer<Leaf>)
    .children;
  const indexOf = (id: string) =>
    siblings.findIndex((child) => child.id === id);
  const downward =
    findLayoutParentId(tree, activeId) === parentId &&
    indexOf(activeId) < indexOf(overId);
  return placeLayoutNodes(tree, nodeIds, overId, downward ? "after" : "before");
}
